import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Button from 'chameleon/packages/blue-book-player/library/button';
import Icon from 'chameleon/packages/blue-book-player/library/icon';
import useHighlights from 'chameleon/packages/blue-book-player/Annotation/hooks/useHighlights';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import {
  preventHighlightPopoverFlicker,
  isSelected,
  isHighlightActive,
} from 'chameleon/packages/blue-book-player/Annotation/utils';
import { HIGHLIGHT_COLORS_NAMES } from 'chameleon/packages/blue-book-player/Annotation/constants';

const HighlightColors = (props) => {
  const [selectedColor, setSelectedColor] = useState(
    HIGHLIGHT_COLORS_NAMES.YELLOW,
  );
  const { selectColor, removeHighlight } = useHighlights();
  const playerEl = useGetPlayerEl();

  const {
    highlightModule,
    learnosityHighlightInfo,
    clickedHighlightColor,
    isAnnotationEnabled,
  } = props;

  useEffect(() => {
    if (!clickedHighlightColor) return;
    setSelectedColor(clickedHighlightColor);
  }, [clickedHighlightColor]);

  const COLORS_CLASSES =
    'rounded-full h-14 w-14 !p-0 border border-bluebook-black1 hover:border-2';
  const onSelectYellow = useCallback(() => {
    if (clickedHighlightColor === HIGHLIGHT_COLORS_NAMES.YELLOW) return;
    preventHighlightPopoverFlicker(() => {
      removeHighlight();
      selectColor(HIGHLIGHT_COLORS_NAMES.YELLOW);
      setSelectedColor(HIGHLIGHT_COLORS_NAMES.YELLOW);
      highlightModule.applyHighlight(learnosityHighlightInfo);
    }, playerEl);
  }, [learnosityHighlightInfo, highlightModule, clickedHighlightColor]);

  const onSelectRed = useCallback(() => {
    if (clickedHighlightColor === HIGHLIGHT_COLORS_NAMES.RED) return;
    preventHighlightPopoverFlicker(() => {
      removeHighlight();
      selectColor(HIGHLIGHT_COLORS_NAMES.RED);
      setSelectedColor(HIGHLIGHT_COLORS_NAMES.RED);
      highlightModule.applyHighlight(learnosityHighlightInfo);
    }, playerEl);
  }, [learnosityHighlightInfo, highlightModule, clickedHighlightColor]);

  const onSelectBlue = useCallback(() => {
    if (clickedHighlightColor === HIGHLIGHT_COLORS_NAMES.BLUE) return;
    preventHighlightPopoverFlicker(() => {
      removeHighlight();
      selectColor(HIGHLIGHT_COLORS_NAMES.BLUE);
      setSelectedColor(HIGHLIGHT_COLORS_NAMES.BLUE);
      highlightModule.applyHighlight(learnosityHighlightInfo);
    }, playerEl);
  }, [learnosityHighlightInfo, highlightModule, clickedHighlightColor]);

  const onSelectYellowDisabled = useCallback(() => {
    selectColor(HIGHLIGHT_COLORS_NAMES.YELLOW);
    setSelectedColor(HIGHLIGHT_COLORS_NAMES.YELLOW);
  }, [learnosityHighlightInfo, highlightModule, clickedHighlightColor]);

  const onSelectRedDisabled = useCallback(() => {
    selectColor(HIGHLIGHT_COLORS_NAMES.RED);
    setSelectedColor(HIGHLIGHT_COLORS_NAMES.RED);
  }, [learnosityHighlightInfo, highlightModule, clickedHighlightColor]);

  const onSelectBlueDisabled = useCallback(() => {
    selectColor(HIGHLIGHT_COLORS_NAMES.BLUE);
    setSelectedColor(HIGHLIGHT_COLORS_NAMES.BLUE);
  }, [learnosityHighlightInfo, highlightModule, clickedHighlightColor]);

  const HIGHILIGHT_COLORS_OPTIONS = [
    {
      color: HIGHLIGHT_COLORS_NAMES.YELLOW,
      backgroundColor: 'bg-bluebook-yellow-highlight',
      fillColor: '!fill-bluebook-yellow-highlight-selected',
      hoverColor: 'hover:bg-bluebook-yellow-highlight-selected',
      onClick:
        isAnnotationEnabled || isHighlightActive()
          ? onSelectYellow
          : onSelectYellowDisabled,
    },
    {
      color: HIGHLIGHT_COLORS_NAMES.BLUE,
      backgroundColor: 'bg-bluebook-blue-highlight',
      fillColor: '!fill-bluebook-blue-highlight-selected',
      hoverColor: 'hover:bg-bluebook-blue-highlight-selected',
      onClick:
        isAnnotationEnabled || isHighlightActive()
          ? onSelectBlue
          : onSelectBlueDisabled,
    },
    {
      color: HIGHLIGHT_COLORS_NAMES.RED,
      backgroundColor: 'bg-bluebook-red-highlight',
      fillColor: '!fill-bluebook-red-highlight-selected',
      hoverColor: 'hover:bg-bluebook-red-highlight-selected',
      onClick:
        isAnnotationEnabled || isHighlightActive()
          ? onSelectRed
          : onSelectRedDisabled,
    },
  ];

  return (
    <>
      {HIGHILIGHT_COLORS_OPTIONS.map((option) => (
        <Button
          key={option.color}
          className={cn(
            COLORS_CLASSES,
            option.backgroundColor,
            option.hoverColor,
            {
              [`${option.selectedColor} border-2`]:
                isSelected(option.color, selectedColor) &&
                (isAnnotationEnabled || isHighlightActive()),
            },
          )}
          onClick={option.onClick}
        >
          {isSelected(option.color, selectedColor) &&
            (isAnnotationEnabled || isHighlightActive()) && (
              <Icon name="highlight_teardrop" className={option.fillColor} />
            )}
        </Button>
      ))}
    </>
  );
};

HighlightColors.propTypes = {
  highlightModule: PropTypes.object,
  learnosityHighlightInfo: PropTypes.object,
  clickedHighlightColor: PropTypes.string,
};

HighlightColors.defaultProps = {
  highlightModule: {},
  learnosityHighlightInfo: {},
  clickedHighlightColor: HIGHLIGHT_COLORS_NAMES.YELLOW,
};

export default HighlightColors;
